/**
 * Runtime validators for @bastion/data shapes.
 *
 * Every config that enters the game (from JSON files, localStorage, or a
 * future API) passes through these guards before it is treated as typed.
 * Violations throw a `DataValidationError` with a path + reason.
 */

import type {
  AchievementDefinition,
  AchievementCondition,
  AchievementMetric,
  BalanceConfig,
  BossConfig,
  CombatTuning,
  DamageType,
  EnemyConfig,
  GameContent,
  EndlessWaveCandidate,
  EndlessWaveGenerationConfig,
  MapConfig,
  SkillConfig,
  SkillType,
  TowerConfig,
  NextAttackEffect,
  WaveConfig,
  AutoSkillConfig,
  SkillReleaseCondition,
  UnitStats,
  PresentationConfig,
} from "./types";

export class DataValidationError extends Error {
  constructor(
    public readonly path: string,
    reason: string,
  ) {
    super(`Invalid data at "${path}": ${reason}`);
    this.name = "DataValidationError";
  }
}

type Predicate = (value: unknown) => boolean;

function assertObject(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new DataValidationError(path, "expected an object");
  }
  return value as Record<string, unknown>;
}

function requireField(
  obj: Record<string, unknown>,
  key: string,
  path: string,
  predicate: Predicate,
): unknown {
  const value = obj[key];
  if (!(key in obj) || !predicate(value)) {
    throw new DataValidationError(`${path}.${key}`, `expected valid value, got ${String(value)}`);
  }
  return value;
}

function requireString(obj: Record<string, unknown>, key: string, path: string): string {
  return requireField(obj, key, path, (v) => typeof v === "string") as string;
}

function requireDamageType(obj: Record<string, unknown>, key: string, path: string): DamageType {
  const value = requireString(obj, key, path);
  const candidate = value as DamageType;
  if (!isDamageType(candidate)) {
    throw new DataValidationError(`${path}.${key}`, `unknown damage type "${value}"`);
  }
  return candidate;
}

function requireNumber(obj: Record<string, unknown>, key: string, path: string): number {
  const value = requireField(
    obj,
    key,
    path,
    (v) => typeof v === "number" && Number.isFinite(v),
  ) as number;
  if (value < 0) {
    throw new DataValidationError(`${path}.${key}`, `expected non-negative, got ${value}`);
  }
  return value;
}

function requireInt(obj: Record<string, unknown>, key: string, path: string): number {
  const value = requireField(
    obj,
    key,
    path,
    (v) => typeof v === "number" && Number.isInteger(v),
  ) as number;
  if (value < 0) {
    throw new DataValidationError(`${path}.${key}`, `expected non-negative integer, got ${value}`);
  }
  return value;
}

function requireArray(obj: Record<string, unknown>, key: string, path: string): unknown[] {
  return requireField(obj, key, path, (v) => Array.isArray(v)) as unknown[];
}

function optionalNumber(
  obj: Record<string, unknown>,
  key: string,
  path: string,
): number | undefined {
  const value = obj[key];
  if (value === undefined) return undefined;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new DataValidationError(`${path}.${key}`, "expected finite number or undefined");
  }
  return value;
}

function optionalUnitStats(
  obj: Record<string, unknown>,
  key: string,
  path: string,
): Partial<UnitStats> | undefined {
  const value = obj[key];
  if (value === undefined) return undefined;
  const stats = assertObject(value, `${path}.${key}`);
  const result: Partial<UnitStats> = {};
  const numericKeys: Array<keyof UnitStats> = [
    "maxHp",
    "defense",
    "magicResistance",
    "accuracy",
    "resistance",
    "attack",
    "magic",
    "attackSpeed",
    "speed",
    "critRate",
    "critDamage",
  ];
  for (const statKey of Object.keys(stats)) {
    if (!numericKeys.includes(statKey as keyof UnitStats)) {
      throw new DataValidationError(`${path}.${key}.${statKey}`, "unknown unit stat");
    }
  }
  for (const statKey of numericKeys) {
    const statValue = stats[statKey];
    if (statValue !== undefined) {
      if (typeof statValue !== "number" || !Number.isFinite(statValue) || statValue < 0) {
        throw new DataValidationError(
          `${path}.${key}.${statKey}`,
          "expected a non-negative finite number",
        );
      }
      result[statKey] = statValue;
    }
  }
  return result;
}

function requireFullUnitStats(obj: Record<string, unknown>, key: string, path: string): UnitStats {
  const value = obj[key];
  if (value === undefined) {
    throw new DataValidationError(`${path}.${key}`, "required field is missing");
  }
  const stats = assertObject(value, `${path}.${key}`);
  const result: UnitStats = {} as UnitStats;
  const requiredKeys: Array<keyof UnitStats> = [
    "maxHp",
    "defense",
    "magicResistance",
    "accuracy",
    "resistance",
    "attack",
    "magic",
    "attackSpeed",
    "speed",
    "critRate",
    "critDamage",
  ];
  for (const statKey of Object.keys(stats)) {
    if (!requiredKeys.includes(statKey as keyof UnitStats)) {
      throw new DataValidationError(`${path}.${key}.${statKey}`, "unknown unit stat");
    }
  }
  for (const statKey of requiredKeys) {
    const statValue = stats[statKey];
    if (statValue === undefined) {
      throw new DataValidationError(`${path}.${key}.${statKey}`, "required field is missing");
    }
    if (typeof statValue !== "number" || !Number.isFinite(statValue) || statValue < 0) {
      throw new DataValidationError(
        `${path}.${key}.${statKey}`,
        "expected a non-negative finite number",
      );
    }
    result[statKey] = statValue;
  }
  return result;
}

function optionalAutoSkill(
  obj: Record<string, unknown>,
  path: string,
): AutoSkillConfig | undefined {
  const value = obj["autoSkill"];
  if (value === undefined) return undefined;
  const autoSkill = assertObject(value, `${path}.autoSkill`);
  const allowedKeys = ["damageMultiplier", "radius"];
  for (const key of Object.keys(autoSkill)) {
    if (!allowedKeys.includes(key))
      throw new DataValidationError(`${path}.autoSkill.${key}`, "unexpected auto skill field");
  }
  return {
    damageMultiplier: requirePositiveNumber(autoSkill, "damageMultiplier", `${path}.autoSkill`),
    radius: requirePositiveNumber(autoSkill, "radius", `${path}.autoSkill`),
  };
}

function requirePositiveNumber(obj: Record<string, unknown>, key: string, path: string): number {
  const value = requireNumber(obj, key, path);
  if (value <= 0) throw new DataValidationError(`${path}.${key}`, "expected a positive number");
  return value;
}

function optionalNonNegativeNumber(
  obj: Record<string, unknown>,
  key: string,
  path: string,
): number | undefined {
  const value = optionalNumber(obj, key, path);
  if (value !== undefined && value < 0)
    throw new DataValidationError(`${path}.${key}`, "expected a non-negative number");
  return value;
}

function requireNumberInRange(
  obj: Record<string, unknown>,
  key: string,
  path: string,
  min: number,
  max: number,
): number {
  const value = requireNumber(obj, key, path);
  if (value < min || value > max)
    throw new DataValidationError(`${path}.${key}`, `expected a number between ${min} and ${max}`);
  return value;
}

function optionalDotOnHit(
  obj: Record<string, unknown>,
  path: string,
): TowerConfig["dotOnHit"] | undefined {
  const value = obj["dotOnHit"];
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new DataValidationError(`${path}.dotOnHit`, "expected an object or undefined");
  }
  const dot = value as Record<string, unknown>;
  return {
    dps: requireNumber(dot, "dps", `${path}.dotOnHit`),
    duration: requireNumber(dot, "duration", `${path}.dotOnHit`),
    damageType: requireDamageType(dot, "damageType", `${path}.dotOnHit`),
  };
}

function optionalNextAttack(
  obj: Record<string, unknown>,
  path: string,
): NextAttackEffect | undefined {
  if (obj["nextAttack"] === undefined) return undefined;
  const nextAttack = assertObject(obj["nextAttack"], `${path}.nextAttack`);
  const kind = requireString(nextAttack, "kind", `${path}.nextAttack`);
  if (kind === "area_damage") {
    const allowedKeys = ["kind", "targetDamageMultiplier", "otherDamageMultiplier", "radius"];
    for (const key of Object.keys(nextAttack)) {
      if (!allowedKeys.includes(key)) {
        throw new DataValidationError(
          `${path}.nextAttack.${key}`,
          "unexpected area damage next attack field",
        );
      }
    }
    return {
      kind,
      targetDamageMultiplier: requirePositiveNumber(
        nextAttack,
        "targetDamageMultiplier",
        `${path}.nextAttack`,
      ),
      otherDamageMultiplier: requirePositiveNumber(
        nextAttack,
        "otherDamageMultiplier",
        `${path}.nextAttack`,
      ),
      radius: requirePositiveNumber(nextAttack, "radius", `${path}.nextAttack`),
    };
  }
  if (kind !== "ice_cone") {
    throw new DataValidationError(
      `${path}.nextAttack.kind`,
      `unknown next attack effect "${kind}"`,
    );
  }
  const allowedKeys = [
    "kind",
    "damageMultiplier",
    "projectileSpeedMultiplier",
    "impactRadiusCells",
    "slowFactor",
    "slowDuration",
    "controlChance",
  ];
  for (const key of Object.keys(nextAttack)) {
    if (!allowedKeys.includes(key))
      throw new DataValidationError(
        `${path}.nextAttack.${key}`,
        "unexpected next attack effect field",
      );
  }
  const damageMultiplier = requirePositiveNumber(
    nextAttack,
    "damageMultiplier",
    `${path}.nextAttack`,
  );
  const projectileSpeedMultiplier = requirePositiveNumber(
    nextAttack,
    "projectileSpeedMultiplier",
    `${path}.nextAttack`,
  );
  const impactRadiusCells = requirePositiveNumber(
    nextAttack,
    "impactRadiusCells",
    `${path}.nextAttack`,
  );
  const slowFactor = requireNumberInRange(nextAttack, "slowFactor", `${path}.nextAttack`, 0, 1);
  const slowDuration = requirePositiveNumber(nextAttack, "slowDuration", `${path}.nextAttack`);
  const controlChance = requireNumberInRange(
    nextAttack,
    "controlChance",
    `${path}.nextAttack`,
    0,
    1,
  );
  return {
    kind,
    damageMultiplier,
    projectileSpeedMultiplier,
    impactRadiusCells,
    slowFactor,
    slowDuration,
    controlChance,
  };
}

function validateSkillReleaseCondition(raw: unknown, path: string): SkillReleaseCondition {
  const condition = assertObject(raw, path);
  const kind = requireString(condition, "kind", path);
  const allowedKeys =
    kind === "attack_count"
      ? ["kind", "count"]
      : kind === "interval"
        ? ["kind", "seconds", "initialDelay"]
        : ["kind"];
  for (const key of Object.keys(condition)) {
    if (!allowedKeys.includes(key))
      throw new DataValidationError(`${path}.${key}`, "unexpected release condition field");
  }
  if (kind === "attack_count") {
    const count = requireInt(condition, "count", path);
    if (count <= 0) throw new DataValidationError(`${path}.count`, "expected a positive integer");
    return { kind, count };
  }
  if (kind === "interval") {
    return {
      kind,
      seconds: requirePositiveNumber(condition, "seconds", path),
      initialDelay: optionalNonNegativeNumber(condition, "initialDelay", path),
    };
  }
  throw new DataValidationError(`${path}.kind`, `unknown skill release condition "${kind}"`);
}

function optionalSkillIds(obj: Record<string, unknown>, path: string): string[] | undefined {
  const value = obj["skillIds"];
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    throw new DataValidationError(`${path}.skillIds`, "expected a non-empty array of skill ids");
  }
  if (value.length === 0) return [];
  const skillIds = value.map((skillId, index) => {
    if (typeof skillId !== "string" || skillId.trim().length === 0) {
      throw new DataValidationError(`${path}.skillIds[${index}]`, "expected a non-empty string");
    }
    return skillId;
  });
  if (new Set(skillIds).size !== skillIds.length) {
    throw new DataValidationError(`${path}.skillIds`, "must not contain duplicate skill ids");
  }
  return skillIds;
}

function rejectLegacySkillId(obj: Record<string, unknown>, path: string): void {
  if ("skillId" in obj) {
    throw new DataValidationError(`${path}.skillId`, "legacy field is not supported; use skillIds");
  }
}

function validateSkillIds(
  content: GameContent,
  skillIds: string[] | undefined,
  path: string,
  expectedType: "TOWER" | "MONSTER",
): void {
  if (skillIds === undefined) return;
  for (const [index, skillId] of skillIds.entries()) {
    const skillPath = `${path}.skillIds[${index}]`;
    const skill = content.skills[skillId];
    if (!skill) throw new DataValidationError(skillPath, `unknown skill "${skillId}"`);
    if (skill.type !== expectedType) {
      throw new DataValidationError(skillPath, `expected ${expectedType} skill, got ${skill.type}`);
    }
  }
}

export function validateTowerConfig(raw: unknown, idHint = "tower"): TowerConfig {
  const obj = assertObject(raw, idHint);
  rejectLegacySkillId(obj, idHint);

  // stats is required and must contain all 11 UnitStats fields
  const stats = requireFullUnitStats(obj, "stats", idHint);

  const attackMode = obj["attackMode"];
  if (attackMode !== undefined && attackMode !== "normal" && attackMode !== "continuous_laser") {
    throw new DataValidationError(`${idHint}.attackMode`, "expected normal or continuous_laser");
  }
  let laserEnhancement: TowerConfig["laserEnhancement"];
  if (obj["laserEnhancement"] !== undefined) {
    const enhancement = assertObject(obj["laserEnhancement"], `${idHint}.laserEnhancement`);
    const pulseCount = requireInt(enhancement, "pulseCount", `${idHint}.laserEnhancement`);
    const damageMultiplier = requireNumber(
      enhancement,
      "damageMultiplier",
      `${idHint}.laserEnhancement`,
    );
    if (pulseCount <= 0) {
      throw new DataValidationError(`${idHint}.laserEnhancement.pulseCount`, "must be positive");
    }
    if (damageMultiplier <= 0) {
      throw new DataValidationError(
        `${idHint}.laserEnhancement.damageMultiplier`,
        "must be positive",
      );
    }
    laserEnhancement = { pulseCount, damageMultiplier };
  }

  if (attackMode === "continuous_laser" && !laserEnhancement) {
    throw new DataValidationError(`${idHint}.laserEnhancement`, "required for continuous_laser");
  }
  if (laserEnhancement && attackMode !== "continuous_laser") {
    throw new DataValidationError(
      `${idHint}.laserEnhancement`,
      "only supported for continuous_laser",
    );
  }
  if (attackMode === "continuous_laser" && obj["damageType"] !== "magic") {
    throw new DataValidationError(`${idHint}.damageType`, "continuous_laser requires magic damage");
  }

  const config: TowerConfig = {
    id: requireString(obj, "id", idHint),
    attackMode,
    laserEnhancement,
    cost: requireNumber(obj, "cost", idHint),
    attackSpeed: requireNumber(obj, "attackSpeed", idHint),
    range: requireNumber(obj, "range", idHint),
    damageType: requireDamageType(obj, "damageType", idHint),
    projectileSpeed: requireNumber(obj, "projectileSpeed", idHint),
    maxLevel: requireInt(obj, "maxLevel", idHint),
    sellRefundRate: requireNumber(obj, "sellRefundRate", idHint),
    stats,
    upgrades: requireArray(obj, "upgrades", idHint).map((u, i) => {
      const uobj = assertObject(u, `${idHint}.upgrades[${i}]`);
      return {
        level: requireInt(uobj, "level", `${idHint}.upgrades[${i}]`),
        attack: optionalNumber(uobj, "attack", `${idHint}.upgrades[${i}]`),
        attackSpeed: optionalNumber(uobj, "attackSpeed", `${idHint}.upgrades[${i}]`),
        range: optionalNumber(uobj, "range", `${idHint}.upgrades[${i}]`),
        upgradeCost: optionalNumber(uobj, "upgradeCost", `${idHint}.upgrades[${i}]`),
        splashRadius: optionalNumber(uobj, "splashRadius", `${idHint}.upgrades[${i}]`),
        slowOnHit: optionalNumber(uobj, "slowOnHit", `${idHint}.upgrades[${i}]`),
        slowDuration: optionalNumber(uobj, "slowDuration", `${idHint}.upgrades[${i}]`),
        chainCount: optionalNumber(uobj, "chainCount", `${idHint}.upgrades[${i}]`),
        dotOnHit: optionalDotOnHit(uobj, `${idHint}.upgrades[${i}]`),
      };
    }),
  };
  const optionalKeys: Array<keyof TowerConfig> = [
    "slowOnHit",
    "slowDuration",
    "dotOnHit",
    "chainCount",
    "splashRadius",
    "bodyRadiusCells",
    "skillIds",
    "position",
  ];
  for (const key of optionalKeys) {
    const value = obj[key];
    if (value !== undefined) {
      if (key === "skillIds") {
        config.skillIds = optionalSkillIds(obj, idHint);
      } else {
        (config as unknown as Record<string, unknown>)[key] = value;
      }
    }
  }
  return config;
}

export function validateEnemyConfig(raw: unknown, idHint = "enemy"): EnemyConfig {
  const obj = assertObject(raw, idHint);
  if ("skillIds" in obj) {
    optionalSkillIds(obj, idHint);
  }
  if ("skillId" in obj || "hpPerWave" in obj) {
    const key = "skillId" in obj ? "skillId" : "hpPerWave";
    throw new DataValidationError(`${idHint}.${key}`, "field is not supported on enemies");
  }

  // stats is required and must contain all 11 UnitStats fields
  const stats = requireFullUnitStats(obj, "stats", idHint);

  const config: EnemyConfig = {
    id: requireString(obj, "id", idHint),
    stats,
    goldReward: requireNumber(obj, "goldReward", idHint),
    livesDamage: requireInt(obj, "livesDamage", idHint),
    isBoss: obj["isBoss"] === true ? true : undefined,
    bodyRadiusCells: optionalNumber(obj, "bodyRadiusCells", idHint),
    dodgeChance: optionalNumber(obj, "dodgeChance", idHint),
    regenInterval: optionalNumber(obj, "regenInterval", idHint),
    regenPercentPerTick: optionalNumber(obj, "regenPercentPerTick", idHint),
    splitOnDeath: optionalNumber(obj, "splitOnDeath", idHint),
    splitInto: obj["splitInto"] === undefined ? undefined : requireString(obj, "splitInto", idHint),
    special: obj["special"] === undefined ? undefined : requireString(obj, "special", idHint),
    skillIds: optionalSkillIds(obj, idHint),
  };
  return config;
}

export function validateBossConfig(raw: unknown, idHint = "boss"): BossConfig {
  const enemy = validateEnemyConfig(raw, idHint);
  if (enemy.isBoss !== true) {
    throw new DataValidationError(`${idHint}.isBoss`, "boss config must set isBoss=true");
  }
  return enemy as BossConfig;
}

export function validateWaveConfig(raw: unknown, idHint = "wave"): WaveConfig {
  const obj = assertObject(raw, idHint);
  const groups = requireArray(obj, "groups", idHint).map((g, i) => {
    const groupObj = assertObject(g, `${idHint}.groups[${i}]`);
    return {
      enemyId: requireString(groupObj, "enemyId", `${idHint}.groups[${i}]`),
      count: requireInt(groupObj, "count", `${idHint}.groups[${i}]`),
      spawnInterval: requireNumber(groupObj, "spawnInterval", `${idHint}.groups[${i}]`),
      delay: requireNumber(groupObj, "delay", `${idHint}.groups[${i}]`),
    };
  });
  return {
    id: requireString(obj, "id", idHint),
    wave: requireInt(obj, "wave", idHint),
    mapId: requireString(obj, "mapId", idHint),
    groups,
    rewardLives: optionalNumber(obj, "rewardLives", idHint),
    rewardGold: optionalNumber(obj, "rewardGold", idHint),
    isFinal: obj["isFinal"] === true ? true : undefined,
  };
}

export function validateMapConfig(raw: unknown, idHint = "map"): MapConfig {
  const obj = assertObject(raw, idHint);
  const buildZones = requireArray(obj, "buildZones", idHint).map((b, i) => {
    const bobj = assertObject(b, `${idHint}.buildZones[${i}]`);
    return {
      id: requireString(bobj, "id", `${idHint}.buildZones[${i}]`),
      x: requireNumber(bobj, "x", `${idHint}.buildZones[${i}]`),
      y: requireNumber(bobj, "y", `${idHint}.buildZones[${i}]`),
      width: requireNumber(bobj, "width", `${idHint}.buildZones[${i}]`),
      height: requireNumber(bobj, "height", `${idHint}.buildZones[${i}]`),
      minSpacing: optionalNumber(bobj, "minSpacing", `${idHint}.buildZones[${i}]`),
    };
  });
  const spawnPoints = requireArray(obj, "spawnPoints", idHint).map((s) => {
    const sobj = assertObject(s, `${idHint}.spawnPoints`);
    return {
      x: requireNumber(sobj, "x", `${idHint}.spawnPoints`),
      y: requireNumber(sobj, "y", `${idHint}.spawnPoints`),
    };
  });
  const base = assertObject(obj["base"], `${idHint}.base`);
  const basePoint = {
    x: requireNumber(base, "x", `${idHint}.base`),
    y: requireNumber(base, "y", `${idHint}.base`),
  };
  return {
    id: requireString(obj, "id", idHint),
    width: requireNumber(obj, "width", idHint),
    height: requireNumber(obj, "height", idHint),
    gridSize: requireNumber(obj, "gridSize", idHint),
    buildZones,
    spawnPoints,
    base: basePoint,
    startGold: requireNumber(obj, "startGold", idHint),
    startLives: requireInt(obj, "startLives", idHint),
    waves: requireArray(obj, "waves", idHint).map((w) => requireStringField(w)),
    enemyStatGrowthPerWave: requireNumber(obj, "enemyStatGrowthPerWave", idHint),
    isEndless: obj["isEndless"] === true ? true : undefined,
  };
}

function requireStringField(value: unknown): string {
  if (typeof value !== "string") {
    throw new DataValidationError("waves[]", "expected string wave id");
  }
  return value;
}

export function validateSkillConfig(raw: unknown, idHint = "skill"): SkillConfig {
  const obj = assertObject(raw, idHint);
  const typeRaw = obj["type"];
  let type: SkillType = "GLOBAL";
  if (typeRaw !== undefined) {
    if (typeRaw !== "GLOBAL" && typeRaw !== "TOWER" && typeRaw !== "MONSTER") {
      throw new DataValidationError(`${idHint}.type`, `unknown skill type "${typeRaw}"`);
    }
    type = typeRaw as SkillType;
  }
  const releaseCondition =
    obj["releaseCondition"] === undefined
      ? undefined
      : validateSkillReleaseCondition(obj["releaseCondition"], `${idHint}.releaseCondition`);
  for (const key of Object.keys(obj)) {
    if (key === "charge" || key === "chargedAttack" || key === "monsterSkill") {
      throw new DataValidationError(`${idHint}.${key}`, "legacy field is not supported");
    }
  }
  if (type === "GLOBAL" && releaseCondition !== undefined) {
    throw new DataValidationError(
      `${idHint}.releaseCondition`,
      "GLOBAL skills must not define a release condition",
    );
  }
  if (type === "MONSTER" && releaseCondition?.kind !== "interval") {
    throw new DataValidationError(
      `${idHint}.releaseCondition`,
      "MONSTER skills require an interval condition",
    );
  }
  const nextAttack = optionalNextAttack(obj, idHint);
  const autoSkill = optionalAutoSkill(obj, idHint);
  if (type !== "TOWER" && nextAttack !== undefined) {
    throw new DataValidationError(
      `${idHint}.nextAttack`,
      "nextAttack effects are only valid for TOWER skills",
    );
  }
  if (type !== "TOWER" && autoSkill !== undefined) {
    throw new DataValidationError(
      `${idHint}.autoSkill`,
      "autoSkill effects are only valid for TOWER skills",
    );
  }
  return {
    id: requireString(obj, "id", idHint),
    type,
    cost: requireNumber(obj, "cost", idHint),
    cooldown: requireNumber(obj, "cooldown", idHint),
    releaseCondition,
    radius: optionalNumber(obj, "radius", idHint),
    damage: optionalNumber(obj, "damage", idHint),
    damageType: optionalDamageType(obj, "damageType", idHint),
    slowFactor: optionalNumber(obj, "slowFactor", idHint),
    slowDuration: optionalNumber(obj, "slowDuration", idHint),
    stunDuration: optionalNumber(obj, "stunDuration", idHint),
    healLives: optionalNumber(obj, "healLives", idHint),
    attackSpeedFactor: optionalNumber(obj, "attackSpeedFactor", idHint),
    attackSpeedDuration: optionalNumber(obj, "attackSpeedDuration", idHint),
    skillDescription:
      obj["skillDescription"] === undefined
        ? undefined
        : (requireString(obj, "skillDescription", idHint) as SkillConfig["skillDescription"]),
    nextAttack,
    autoSkill,
    control: optionalControl(obj, idHint),
    stats: optionalUnitStats(obj, "stats", idHint),
    icon: obj["icon"] === undefined ? undefined : requireString(obj, "icon", idHint),
  };
}

function optionalControl(
  obj: Record<string, unknown>,
  path: string,
): SkillConfig["control"] | undefined {
  const value = obj["control"];
  if (value === undefined) return undefined;
  const control = assertObject(value, `${path}.control`);
  const type = requireString(control, "type", `${path}.control`);
  if (!["SLOW", "STUN", "ROOT", "SILENCE"].includes(type)) {
    throw new DataValidationError(`${path}.control.type`, `unknown control type "${type}"`);
  }
  return {
    type: type as NonNullable<SkillConfig["control"]>["type"],
    duration: requirePositiveNumber(control, "duration", `${path}.control`),
    chance: requireNumberInRange(control, "chance", `${path}.control`, 0, 1),
  };
}

function optionalDamageType(
  obj: Record<string, unknown>,
  key: string,
  path: string,
): DamageType | undefined {
  const value = obj[key];
  if (value === undefined) return undefined;
  if (typeof value !== "string") {
    throw new DataValidationError(`${path}.${key}`, "expected string or undefined");
  }
  if (!isDamageType(value)) {
    throw new DataValidationError(`${path}.${key}`, `unknown damage type "${value}"`);
  }
  return value as DamageType;
}

export function validateBalanceConfig(raw: unknown, idHint = "balance"): BalanceConfig {
  const obj = assertObject(raw, idHint);
  return {
    id: requireString(obj, "id", idHint),
    globalDamageMultiplier: optionalNumber(obj, "globalDamageMultiplier", idHint),
    globalEnemyHpMultiplier: optionalNumber(obj, "globalEnemyHpMultiplier", idHint),
    globalEnemySpeedMultiplier: optionalNumber(obj, "globalEnemySpeedMultiplier", idHint),
    goldRewardMultiplier: optionalNumber(obj, "goldRewardMultiplier", idHint),
    startingGoldMultiplier: optionalNumber(obj, "startingGoldMultiplier", idHint),
    startingLivesMultiplier: optionalNumber(obj, "startingLivesMultiplier", idHint),
    enemyCountMultiplier: optionalNumber(obj, "enemyCountMultiplier", idHint),
  };
}

export function validateCombatTuning(raw: unknown, idHint = "combatTuning"): CombatTuning {
  const obj = assertObject(raw, idHint);
  for (const key of Object.keys(obj)) {
    if (key === "monsterSkill" || key === "charge")
      throw new DataValidationError(`${idHint}.${key}`, "legacy field is not supported");
  }
  const auto = assertObject(obj["towerAutoSkill"], `${idHint}.towerAutoSkill`);
  return {
    id: requireString(obj, "id", idHint),
    splashDamageMultiplier: requireNumberInRange(obj, "splashDamageMultiplier", idHint, 0, 1),
    chainDamageMultiplier: requireNumberInRange(obj, "chainDamageMultiplier", idHint, 0, 1),
    towerAutoSkill: {
      damageMultiplier: requirePositiveNumber(auto, "damageMultiplier", `${idHint}.towerAutoSkill`),
      attackSpeedFactor: optionalNumber(auto, "attackSpeedFactor", `${idHint}.towerAutoSkill`),
      attackSpeedDuration: optionalNumber(auto, "attackSpeedDuration", `${idHint}.towerAutoSkill`),
    },
  };
}

export function validateAchievementDefinitions(
  raw: unknown,
  idHint = "achievements",
): AchievementDefinition[] {
  const obj = assertObject(raw, idHint);
  return requireArray(obj, "definitions", idHint).map((value, index) => {
    const item = assertObject(value, `${idHint}.definitions[${index}]`);
    const condition = assertObject(item["condition"], `${idHint}.definitions[${index}].condition`);
    const definition: AchievementDefinition = {
      id: requireString(item, "id", `${idHint}.definitions[${index}]`),
      condition: validateAchievementCondition(
        condition,
        `${idHint}.definitions[${index}].condition`,
      ),
      rewardGold: requireNumber(item, "rewardGold", `${idHint}.definitions[${index}]`),
    };
    return definition;
  });
}

function validateAchievementCondition(
  raw: Record<string, unknown>,
  path: string,
): AchievementCondition {
  const kind = raw["kind"];
  if (kind === "speedDemon" || kind === "noDamage" || kind === "collector") {
    if (kind === "speedDemon") return { kind, value: requirePositiveNumber(raw, "value", path) };
    return { kind };
  }
  const metric = requireString(raw, "metric", path);
  const operator = requireString(raw, "operator", path);
  if (
    ![
      "enemiesKilled",
      "currentWave",
      "towersBuiltTotal",
      "goldEarned",
      "livesLostThisGame",
      "wave1CompleteTime",
      "elapsedTime",
    ].includes(metric)
  ) {
    throw new DataValidationError(`${path}.metric`, `unknown metric "${metric}"`);
  }
  if (![">=", ">", "<", "==="].includes(operator)) {
    throw new DataValidationError(`${path}.operator`, `unknown operator "${operator}"`);
  }
  return {
    metric: metric as AchievementMetric,
    operator: operator as ">=" | ">" | "<" | "===",
    value: requireNumber(raw, "value", path),
  };
}

export function validatePresentationConfig(
  raw: unknown,
  idHint = "presentation",
): PresentationConfig {
  const obj = assertObject(raw, idHint);
  return {
    id: requireString(obj, "id", idHint),
    enemyFastSpeedThreshold: requirePositiveNumber(obj, "enemyFastSpeedThreshold", idHint),
    enemyTankyHpThreshold: requirePositiveNumber(obj, "enemyTankyHpThreshold", idHint),
  };
}
function isDamageType(value: string): boolean {
  return ["physical", "magic"].includes(value);
}

/**
 * Validates a full `GameContent` bundle. Every record key is validated
 * individually; a single bad entry throws, not silently skips.
 */
function validateEndlessWaveGenerationConfig(
  raw: unknown,
  path: string,
): EndlessWaveGenerationConfig {
  const obj = assertObject(raw, path);
  assertExactKeys(
    obj,
    [
      "startWave",
      "perlin",
      "ordinaryRoster",
      "bossRoster",
      "adjacency",
      "generation",
      "rewardSchedule",
    ],
    path,
  );

  const startWave = requireInt(obj, "startWave", path);
  if (startWave <= 0) {
    throw new DataValidationError(`${path}.startWave`, "expected a positive integer");
  }

  const perlin = assertObject(obj["perlin"], `${path}.perlin`);
  assertExactKeys(perlin, ["scale", "pressure"], `${path}.perlin`);
  const scale = validateEndlessRange(perlin["scale"], `${path}.perlin.scale`, 1, 128, true);
  const pressure = validateEndlessRange(perlin["pressure"], `${path}.perlin.pressure`, 0, 1, false);
  const ordinaryRoster = validateEndlessRoster(obj["ordinaryRoster"], `${path}.ordinaryRoster`);
  const bossRoster = validateEndlessRoster(obj["bossRoster"], `${path}.bossRoster`);
  if (ordinaryRoster.length === 0 || bossRoster.length === 0) {
    throw new DataValidationError(path, "ordinaryRoster and bossRoster must not be empty");
  }

  const adjacencyObj = assertObject(obj["adjacency"], `${path}.adjacency`);
  const adjacency: Record<string, string[]> = {};
  for (const [candidateId, compatibleRaw] of Object.entries(adjacencyObj)) {
    if (!Array.isArray(compatibleRaw)) {
      throw new DataValidationError(`${path}.adjacency.${candidateId}`, "expected an array");
    }
    const compatibleIds = compatibleRaw.map((id, index) => {
      if (typeof id !== "string" || id.trim().length === 0) {
        throw new DataValidationError(
          `${path}.adjacency.${candidateId}[${index}]`,
          "expected a non-empty candidate id",
        );
      }
      return id;
    });
    if (new Set(compatibleIds).size !== compatibleIds.length) {
      throw new DataValidationError(
        `${path}.adjacency.${candidateId}`,
        "must not contain duplicate compatibility ids",
      );
    }
    adjacency[candidateId] = compatibleIds;
  }

  const generationObj = assertObject(obj["generation"], `${path}.generation`);
  assertExactKeys(
    generationObj,
    ["groupSlots", "groupCount", "spawnInterval", "delay", "maxBossesPerWave"],
    `${path}.generation`,
  );
  const groupSlots = validateEndlessRange(
    generationObj["groupSlots"],
    `${path}.generation.groupSlots`,
    1,
    4,
    true,
  );
  const groupCount = validateEndlessRange(
    generationObj["groupCount"],
    `${path}.generation.groupCount`,
    1,
    28,
    true,
  );
  const spawnInterval = validateEndlessRange(
    generationObj["spawnInterval"],
    `${path}.generation.spawnInterval`,
    0,
    60,
    false,
  );
  if (spawnInterval.min <= 0) {
    throw new DataValidationError(
      `${path}.generation.spawnInterval.min`,
      "expected a positive number",
    );
  }
  const delay = validateEndlessRange(
    generationObj["delay"],
    `${path}.generation.delay`,
    0,
    120,
    false,
  );
  const maxBossesPerWave = requireInt(generationObj, "maxBossesPerWave", `${path}.generation`);
  if (maxBossesPerWave < 0 || maxBossesPerWave > 4) {
    throw new DataValidationError(
      `${path}.generation.maxBossesPerWave`,
      "expected an integer between 0 and 4",
    );
  }
  if (groupSlots.max * groupCount.max + maxBossesPerWave > 113) {
    throw new DataValidationError(
      `${path}.generation`,
      "maximum planned enemy count per wave must not exceed 113",
    );
  }

  const rewardSchedule = requireArray(obj, "rewardSchedule", path).map((entry, index) => {
    const entryPath = `${path}.rewardSchedule[${index}]`;
    const reward = assertObject(entry, entryPath);
    assertExactKeys(reward, ["waveFrom", "waveTo", "rewardGold"], entryPath);
    return {
      waveFrom: requireInt(reward, "waveFrom", entryPath),
      waveTo: requireInt(reward, "waveTo", entryPath),
      rewardGold: requireNumber(reward, "rewardGold", entryPath),
    };
  });
  if (rewardSchedule.length === 0) {
    throw new DataValidationError(`${path}.rewardSchedule`, "must not be empty");
  }
  let expectedWave = startWave;
  for (const [index, tier] of rewardSchedule.entries()) {
    if (
      tier.waveFrom !== expectedWave ||
      tier.waveTo < tier.waveFrom ||
      tier.waveTo > 9999 ||
      tier.rewardGold < 0
    ) {
      throw new DataValidationError(
        `${path}.rewardSchedule[${index}]`,
        `expected contiguous valid reward tier starting at wave ${expectedWave}`,
      );
    }
    expectedWave = tier.waveTo + 1;
  }
  if (rewardSchedule.at(-1)?.waveTo !== 9999) {
    throw new DataValidationError(`${path}.rewardSchedule`, "must end at wave 9999");
  }

  return {
    startWave,
    perlin: { scale, pressure },
    ordinaryRoster,
    bossRoster,
    adjacency,
    generation: { groupSlots, groupCount, spawnInterval, delay, maxBossesPerWave },
    rewardSchedule,
  };
}

function assertExactKeys(obj: Record<string, unknown>, keys: string[], path: string): void {
  for (const key of Object.keys(obj)) {
    if (!keys.includes(key)) {
      throw new DataValidationError(`${path}.${key}`, "unexpected field");
    }
  }
}

function validateEndlessRange(
  raw: unknown,
  path: string,
  minBound: number,
  maxBound: number,
  integer: boolean,
): { min: number; max: number } {
  const obj = assertObject(raw, path);
  assertExactKeys(obj, ["min", "max"], path);
  const min = requireNumber(obj, "min", path);
  const max = requireNumber(obj, "max", path);
  if (
    (integer && (!Number.isInteger(min) || !Number.isInteger(max))) ||
    min < minBound ||
    max > maxBound ||
    min > max
  ) {
    throw new DataValidationError(
      path,
      `expected an ordered range within ${minBound}..${maxBound}${integer ? " using integers" : ""}`,
    );
  }
  return { min, max };
}

function validateEndlessRoster(raw: unknown, path: string): EndlessWaveCandidate[] {
  if (!Array.isArray(raw)) {
    throw new DataValidationError(path, "expected an array");
  }
  const candidates = raw.map((entry, index) => {
    const entryPath = `${path}[${index}]`;
    const candidate = assertObject(entry, entryPath);
    assertExactKeys(candidate, ["enemyId", "baseWeight", "noiseAffinity"], entryPath);
    const noiseAffinity = requireNumberInRange(candidate, "noiseAffinity", entryPath, 0, 1);
    if (noiseAffinity <= 0) {
      throw new DataValidationError(`${entryPath}.noiseAffinity`, "expected a number in (0, 1]");
    }
    return {
      enemyId: requireString(candidate, "enemyId", entryPath),
      baseWeight: requirePositiveNumber(candidate, "baseWeight", entryPath),
      noiseAffinity,
    };
  });
  if (new Set(candidates.map((candidate) => candidate.enemyId)).size !== candidates.length) {
    throw new DataValidationError(path, "candidate ids must be unique");
  }
  return candidates;
}

function validateEndlessWaves(raw: unknown): Record<string, EndlessWaveGenerationConfig> {
  const record = assertObject(raw, "endlessWaves");
  return Object.fromEntries(
    Object.entries(record).map(([mapId, config]) => [
      mapId,
      validateEndlessWaveGenerationConfig(config, `endlessWaves.${mapId}`),
    ]),
  );
}

/** 校验无尽波次配置的候选集合、地图类型与生成引用。 */
function validateEndlessWaveReferences(
  content: GameContent,
  configByMap: Record<string, EndlessWaveGenerationConfig>,
): void {
  for (const [mapId, config] of Object.entries(configByMap)) {
    const map = content.maps[mapId];
    if (!map) {
      throw new DataValidationError(`endlessWaves.${mapId}`, `unknown map "${mapId}"`);
    }
    if (map.isEndless !== true) {
      throw new DataValidationError(
        `endlessWaves.${mapId}`,
        "endless config requires an endless map",
      );
    }
    if (config.startWave !== map.waves.length + 1) {
      throw new DataValidationError(
        `endlessWaves.${mapId}.startWave`,
        `must follow the ${map.waves.length} configured map waves`,
      );
    }

    const ordinaryIds = config.ordinaryRoster.map((candidate) => candidate.enemyId);
    const bossIds = config.bossRoster.map((candidate) => candidate.enemyId);
    for (const [index, candidate] of config.ordinaryRoster.entries()) {
      if (!content.enemies[candidate.enemyId]) {
        const candidatePath = `endlessWaves.${mapId}.ordinaryRoster[${index}].enemyId`;
        if (content.bosses[candidate.enemyId]) {
          throw new DataValidationError(
            candidatePath,
            `expected an ordinary enemy, got Boss "${candidate.enemyId}"`,
          );
        }
        throw new DataValidationError(candidatePath, `unknown enemy "${candidate.enemyId}"`);
      }
      if (content.enemies[candidate.enemyId].isBoss) {
        throw new DataValidationError(
          `endlessWaves.${mapId}.ordinaryRoster[${index}].enemyId`,
          `expected an ordinary enemy, got Boss "${candidate.enemyId}"`,
        );
      }
    }
    for (const [index, candidate] of config.bossRoster.entries()) {
      if (!content.bosses[candidate.enemyId]) {
        const candidatePath = `endlessWaves.${mapId}.bossRoster[${index}].enemyId`;
        if (content.enemies[candidate.enemyId]) {
          throw new DataValidationError(
            candidatePath,
            `expected a Boss, got enemy "${candidate.enemyId}"`,
          );
        }
        throw new DataValidationError(candidatePath, `unknown Boss "${candidate.enemyId}"`);
      }
    }
    if (
      ordinaryIds.length !== Object.keys(content.enemies).length ||
      ordinaryIds.some((id) => !content.enemies[id]) ||
      Object.keys(content.enemies).some((id) => !ordinaryIds.includes(id))
    ) {
      throw new DataValidationError(
        `endlessWaves.${mapId}.ordinaryRoster`,
        "must contain every ordinary enemy exactly once",
      );
    }
    if (
      bossIds.length !== Object.keys(content.bosses).length ||
      bossIds.some((id) => !content.bosses[id]) ||
      Object.keys(content.bosses).some((id) => !bossIds.includes(id))
    ) {
      throw new DataValidationError(
        `endlessWaves.${mapId}.bossRoster`,
        "must contain every Boss exactly once",
      );
    }

    const candidateIds = [...ordinaryIds, ...bossIds];
    const adjacency = config.adjacency;
    if (
      Object.keys(adjacency).length !== candidateIds.length ||
      candidateIds.some((candidateId) => !(candidateId in adjacency)) ||
      Object.keys(adjacency).some((candidateId) => !candidateIds.includes(candidateId))
    ) {
      throw new DataValidationError(
        `endlessWaves.${mapId}.adjacency`,
        "must define exactly one adjacency list for every roster candidate",
      );
    }
    for (const [candidateId, compatibleIds] of Object.entries(adjacency)) {
      if (!compatibleIds.includes(candidateId)) {
        throw new DataValidationError(
          `endlessWaves.${mapId}.adjacency.${candidateId}`,
          "must include itself to guarantee a valid repeated-candidate fallback",
        );
      }
      for (const compatibleId of compatibleIds) {
        if (!candidateIds.includes(compatibleId)) {
          throw new DataValidationError(
            `endlessWaves.${mapId}.adjacency.${candidateId}`,
            `unknown compatibility candidate "${compatibleId}"`,
          );
        }
        if (!adjacency[compatibleId]?.includes(candidateId)) {
          throw new DataValidationError(
            `endlessWaves.${mapId}.adjacency.${candidateId}`,
            `compatibility with "${compatibleId}" must be symmetric`,
          );
        }
      }
    }
  }
}

export function validateGameContent(raw: unknown): GameContent {
  const obj = assertObject(raw, "content");
  const content: GameContent = {
    towers: collectValidated<TowerConfig>(obj["towers"], "towers", validateTowerConfig),
    enemies: collectValidated<EnemyConfig>(obj["enemies"], "enemies", validateEnemyConfig),
    bosses: collectValidated<BossConfig>(obj["bosses"], "bosses", validateBossConfig),
    waves: collectValidated<WaveConfig>(obj["waves"], "waves", validateWaveConfig),
    maps: collectValidated<MapConfig>(obj["maps"], "maps", validateMapConfig),
    skills: collectValidated<SkillConfig>(obj["skills"], "skills", validateSkillConfig),
    balance: validateBalanceConfig(obj["balance"], "balance"),
  };
  const endlessWavesRaw = obj["endlessWaves"];
  if (endlessWavesRaw !== undefined) {
    content.endlessWaves = validateEndlessWaves(endlessWavesRaw);
  }
  const combatRaw = obj["combatTuning"];
  if (combatRaw !== undefined) content.combatTuning = validateCombatTuning(combatRaw);
  const achievementsRaw = obj["achievements"];
  if (achievementsRaw !== undefined)
    content.achievements = validateAchievementDefinitions(
      { definitions: achievementsRaw },
      "achievements",
    );
  const presentationRaw = obj["presentation"];
  if (presentationRaw !== undefined)
    content.presentation = validatePresentationConfig(presentationRaw);
  const difficultyBalances = obj["difficultyBalances"];
  if (difficultyBalances !== undefined) {
    const balances = assertObject(difficultyBalances, "content.difficultyBalances");
    content.difficultyBalances = {};
    for (const key of ["easy", "normal", "hard"] as const) {
      const value = balances[key];
      if (value !== undefined)
        content.difficultyBalances[key] = validateBalanceConfig(value, `difficultyBalances.${key}`);
    }
  }
  validateReferences(content);
  if (content.endlessWaves) validateEndlessWaveReferences(content, content.endlessWaves);
  return content;
}

function validateReferences(content: GameContent): void {
  for (const [key, skill] of Object.entries(content.skills)) {
    if (skill.id !== key)
      throw new DataValidationError(`skills.${key}.id`, `must match record key "${key}"`);
    if (skill.nextAttack && skill.type !== "TOWER") {
      throw new DataValidationError(
        `skills.${key}.nextAttack`,
        "nextAttack effects are only valid for TOWER skills",
      );
    }
  }
  for (const [key, tower] of Object.entries(content.towers)) {
    if (tower.id !== key)
      throw new DataValidationError(`towers.${key}.id`, `must match record key "${key}"`);
    validateSkillIds(content, tower.skillIds, `towers.${key}`, "TOWER");
    if (tower.upgrades.length === 0 || tower.upgrades[0].level !== 1) {
      throw new DataValidationError(`towers.${key}.upgrades`, "must start at level 1");
    }
  }
  for (const [key, enemy] of Object.entries(content.enemies)) {
    if (enemy.id !== key)
      throw new DataValidationError(`enemies.${key}.id`, `must match record key "${key}"`);
    validateEnemyReferences(content, enemy, `enemies.${key}`);
  }
  for (const [key, boss] of Object.entries(content.bosses)) {
    if (boss.id !== key)
      throw new DataValidationError(`bosses.${key}.id`, `must match record key "${key}"`);
    validateEnemyReferences(content, boss, `bosses.${key}`);
  }
  for (const [key, wave] of Object.entries(content.waves)) {
    if (wave.id !== key)
      throw new DataValidationError(`waves.${key}.id`, `must match record key "${key}"`);
    for (const group of wave.groups) {
      if (!content.enemies[group.enemyId] && !content.bosses[group.enemyId]) {
        throw new DataValidationError(`waves.${key}.groups`, `unknown enemy "${group.enemyId}"`);
      }
    }
  }
  for (const [key, map] of Object.entries(content.maps)) {
    if (map.id !== key)
      throw new DataValidationError(`maps.${key}.id`, `must match record key "${key}"`);
    for (const waveId of map.waves) {
      const wave = content.waves[waveId];
      if (!wave) throw new DataValidationError(`maps.${key}.waves`, `unknown wave "${waveId}"`);
      if (wave.mapId !== map.id)
        throw new DataValidationError(
          `maps.${key}.waves`,
          `wave "${waveId}" belongs to "${wave.mapId}"`,
        );
    }
  }
}

function validateEnemyReferences(content: GameContent, enemy: EnemyConfig, path: string): void {
  validateSkillIds(content, enemy.skillIds, path, "MONSTER");
  const hasRegenInterval = enemy.regenInterval !== undefined;
  const hasRegenPercent = enemy.regenPercentPerTick !== undefined;
  if (hasRegenInterval !== hasRegenPercent) {
    throw new DataValidationError(
      path,
      "regenInterval and regenPercentPerTick must be provided together",
    );
  }
  if (enemy.splitInto && !content.enemies[enemy.splitInto] && !content.bosses[enemy.splitInto]) {
    throw new DataValidationError(`${path}.splitInto`, `unknown enemy "${enemy.splitInto}"`);
  }
  if (enemy.splitOnDeath !== undefined && !enemy.splitInto) {
    throw new DataValidationError(`${path}.splitOnDeath`, "requires splitInto");
  }
}

function collectValidated<T>(
  rawRecord: unknown,
  recordName: string,
  validator: (value: unknown, idHint: string) => T,
): Record<string, T> {
  const record = assertObject(rawRecord, recordName);
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    out[key] = validator(value, `${recordName}.${key}`);
  }
  return out;
}
